import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import api from "../../api";

export default function SkinsResults() {
  const [event, setEvent] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState(false);
  const [toast, setToast] = useState("");
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 4000); };
  const prizesRef = React.useRef({ nearest_pin: "", nearest_pin2: "", longest_drive: "" });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const eventRes = await api.get("/events/current");
      if (!eventRes.data) { setLoading(false); return; }
      setEvent(eventRes.data);

      const [scoresRes, groupsRes, holesRes, prizesRes] = await Promise.all([
        api.get("/scores/" + eventRes.data.id),
        api.get("/groups"),
        api.get("/courses/" + eventRes.data.course_id + "/holes"),
        api.get("/events/prizes").catch(() => ({ data: { nearest_pin: "", nearest_pin2: "", longest_drive: "" } }))
      ]);

      const players = (groupsRes.data.players || []).filter(p => p.playing);
      const holes = holesRes.data;
      const scores = scoresRes.data;

      const skinCount = {};
      players.forEach(p => { skinCount[p.player_id] = { player_id: p.player_id, name: p.name, handicap: p.handicap, hcp_adj: p.hcp_adj ?? p.handicap, skins: 0, gross: 0, net: 0, holeGross: [] }; });

      holes.forEach(hole => {
        const holeScores = players.map(p => {
          const s = scores.find(s => s.player_id === p.player_id && s.hole === hole.hole);
          if (!s) return null;
          const hcp = (p.hcp_adj ?? p.handicap)||0;
          const base = Math.floor(hcp/18);
          const extra = hcp%18;
          const shots = base + (hole.stroke_index <= extra ? 1 : 0);
          const g = parseInt(s.gross);
          return { player_id: p.player_id, g, net: g - shots };
        }).filter(s => s !== null);

        players.forEach(p => {
          const rec = skinCount[p.player_id];
          const own = holeScores.find(hs => hs.player_id === p.player_id);
          rec.gross += own ? own.g : 0;
          rec.net += own ? own.net : 0;
          rec.holeGross.push(own ? own.g : 0);
        });

        if (holeScores.length === 0) return;
        const minNet = Math.min(...holeScores.map(s => s.net));
        const winners = holeScores.filter(s => s.net === minNet);
        if (winners.length === 1) skinCount[winners[0].player_id].skins++;
      });

      const skinsArr = Object.values(skinCount).sort((a,b) => b.skins - a.skins);
      skinsArr.forEach((p,i) => { p.pos = i+1; });
      setResults(skinsArr);

      prizesRef.current = {
        nearest_pin: prizesRes.data.nearest_pin || "",
        nearest_pin2: prizesRes.data.nearest_pin2 || "",
        longest_drive: prizesRes.data.longest_drive || "",
      };
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const archiveResults = async () => {
    if (!event || results.length === 0) return;
    if (!window.confirm("Archive these Skins results? This locks in the round permanently.")) return;
    setArchiving(true);
    try {
      const prizes = prizesRef.current;
      // Skins Won is the format's actual scoring metric, so it's what gets
      // recorded as "points" for the permanent record — same field every
      // other format uses for its main result.
      const archiveData = results.map(p => ({
        position: p.pos, rank: p.pos, player_name: p.name, handicap: p.handicap,
        gross: p.gross, net: p.net, points: p.skins,
        holes: (p.holeGross || []).map((g, i) => ({ hole: i + 1, gross: g })).filter(hh => hh.gross > 0),
      }));
      const twos = [];
      results.forEach(p => { (p.holeGross || []).forEach((g, i) => { if (g === 2) twos.push({ player_name: p.name, hole: i + 1 }); }); });
      const res = await api.post("/archive/save", { event_id: event.id, results: archiveData, ...prizes, twos });
      showToast("Results archived!");
      const changes = res.data?.handicapChanges;
      if (changes && changes.length) {
        const lines = changes.map(c => `${c.name}: ${c.from} → ${c.to}`).join("\n");
        window.alert("Handicaps adjusted:\n\n" + lines);
      }
    } catch(e) { showToast("Failed: " + (e.response?.data?.error || e.message)); }
    finally { setArchiving(false); }
  };

  return (
    <Layout>
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="font-semibold text-sm">Skins Results</span>
          {event && <span className="text-xs text-blue-600 font-medium">{event.game_type}</span>}
          <button onClick={archiveResults} disabled={archiving || results.length === 0}
            className="ml-auto bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white text-xs font-semibold px-4 py-1.5 rounded">
            {archiving ? "Archiving..." : "Archive Results"}
          </button>
        </div>
        {toast && <div className="bg-blue-50 text-blue-700 px-4 py-2 text-sm border-b border-blue-200">{toast}</div>}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-green-800 text-white">
              <tr>
                <th className="px-4 py-2 text-center w-12">Pos</th>
                <th className="px-4 py-2 text-left">Player</th>
                <th className="px-4 py-2 text-center w-16" title="Course Handicap, adjusted for tee and capped per event settings">Course HCP</th>
                <th className="px-4 py-2 text-center w-20">Skins Won</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r,i) => (
                <tr key={r.name} className={"border-b border-gray-200 " + (i%2===0?"bg-white":"bg-green-50")}>
                  <td className="px-4 py-2 text-center font-bold">{r.pos}</td>
                  <td className="px-4 py-2">{r.name}</td>
                  <td className="px-4 py-2 text-center">{r.hcp_adj}</td>
                  <td className="px-4 py-2 text-center font-bold text-blue-700">{r.skins}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && results.length === 0 && <div className="text-center py-8 text-gray-500">No scores entered yet.</div>}
        </div>
      </div>
    </Layout>
  );
}
